import { Refresh, WarningAmberOutlined } from "@mui/icons-material";
import React, { Component } from "react";

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: "100vh",
        backgroundColor: "#F6F6F6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500&display=swap"
          rel="stylesheet"
        />

        <div style={{ width: "100%", maxWidth: "480px", display: "flex", flexDirection: "column", gap: "1rem" }}>

          {/* ── Brand mark ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "0.5rem" }}>
            <div style={{
              width: "28px", height: "28px", borderRadius: "7px",
              backgroundColor: "#1D9E75",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="15" height="15" viewBox="0 0 18 18" fill="none">
                <path d="M3 9h5M9 3v5M9 9l4 4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="9" cy="9" r="7" stroke="#fff" strokeWidth="1.2" opacity="0.4" />
              </svg>
            </div>
            <span style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: "16px", fontWeight: 400,
              color: "#0f1a13",
            }}>
              PesaTrack
            </span>
          </div>

          {/* ── Error card ── */}
          <div style={{
            backgroundColor: "#ffffff",
            border: "1px solid rgba(226,75,74,0.2)",
            borderRadius: "14px",
            overflow: "hidden",
          }}>

            {/* Card header */}
            <div style={{
              padding: "1.5rem 1.75rem 1.25rem",
              borderBottom: "1px solid rgba(226,75,74,0.1)",
              backgroundColor: "rgba(226,75,74,0.03)",
              display: "flex", alignItems: "flex-start", gap: "14px",
            }}>
              <div style={{
                width: "40px", height: "40px", borderRadius: "10px",
                backgroundColor: "rgba(226,75,74,0.08)",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, marginTop: "2px",
              }}>
                <WarningAmberOutlined style={{ fontSize: "20px", color: "#e24b4a" }} />
              </div>
              <div>
                <h1 style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: "22px", fontWeight: 400,
                  color: "#111", margin: 0, lineHeight: 1.25,
                }}>
                  Something went{" "}
                  <em style={{ fontStyle: "italic", color: "#a32d2d" }}>wrong</em>
                </h1>
                <p style={{ fontSize: "13px", color: "#888", margin: "4px 0 0" }}>
                  An unexpected error occurred in the application.
                </p>
              </div>
            </div>

            {/* Error message */}
            <div style={{ padding: "1.25rem 1.75rem" }}>
              {this.state.error && (
                <div style={{
                  backgroundColor: "rgba(226,75,74,0.04)",
                  border: "1px solid rgba(226,75,74,0.15)",
                  borderRadius: "8px",
                  padding: "0.875rem 1rem",
                  marginBottom: "1.25rem",
                }}>
                  <p style={{
                    fontSize: "10px", fontWeight: 500,
                    letterSpacing: "0.08em", textTransform: "uppercase",
                    color: "#e24b4a", margin: "0 0 6px",
                  }}>
                    Error details
                  </p>
                  <p style={{
                    fontFamily: "monospace",
                    fontSize: "12px",
                    color: "#a32d2d",
                    margin: 0,
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                  }}>
                    {this.state.error.message}
                  </p>
                </div>
              )}

              <p style={{ fontSize: "13px", color: "#666", margin: 0, lineHeight: 1.7 }}>
                Try clicking the button below to recover. If the problem persists,
                refresh the page or contact your system administrator.
              </p>
            </div>

            {/* Actions */}
            <div style={{
              padding: "1rem 1.75rem 1.5rem",
              display: "flex", gap: "10px", alignItems: "center",
              borderTop: "1px solid rgba(0,0,0,0.05)",
              backgroundColor: "#fafafa",
            }}>
              <button
                onClick={this.handleReset}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "9px 18px",
                  backgroundColor: "#1D9E75", color: "#fff",
                  border: "none", borderRadius: "8px",
                  fontSize: "13px", fontWeight: 500,
                  fontFamily: "'DM Sans', sans-serif",
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0F6E56"; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#1D9E75"; }}
              >
                <Refresh style={{ fontSize: "15px" }} />
                Try again
              </button>

              <button
                onClick={() => window.location.reload()}
                style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  padding: "9px 18px",
                  backgroundColor: "transparent", color: "#888",
                  border: "1px solid rgba(0,0,0,0.12)", borderRadius: "8px",
                  fontSize: "13px", fontWeight: 500,
                  fontFamily: "'DM Sans', sans-serif",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(0,0,0,0.25)";
                  e.currentTarget.style.color = "#333";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(0,0,0,0.12)";
                  e.currentTarget.style.color = "#888";
                }}
              >
                Reload page
              </button>
            </div>
          </div>

          {/* ── Footer ── */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
            <div style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: "#1D9E75" }} />
            <p style={{ fontSize: "11px", color: "#aaa", margin: 0 }}>
              © 2026 PesaTrack — If this keeps happening, contact support
            </p>
          </div>
        </div>
      </div>
    );
  }
}